/**
 * Shared Metrics Utilities
 *
 * This file provides a single source of truth for common metric calculations
 * used across multiple roles (Owner, Investor, PM, Vendor).
 *
 * All calculations follow standardized formulas to ensure consistency
 * throughout the application.
 */

import type { Property, MaintenanceTicket, Bill, Receivable, Unit } from '@repo/shared-types';
import { isWorkOrderActive } from './statusHelpers';

/**
 * Calculate portfolio-level occupancy rate using weighted average by unit count
 *
 * This is the CORRECT formula for portfolio occupancy - it weights each property's
 * occupancy by its number of units, providing an accurate aggregate view.
 *
 * Example:
 * - Property A: 100 units, 90% occupied = 90 occupied units
 * - Property B: 10 units, 50% occupied = 5 occupied units
 * - Portfolio: 95 occupied / 110 total = 86.4% (NOT the simple average of 70%)
 *
 * @param properties - Array of properties to calculate occupancy for
 * @returns Portfolio occupancy rate as a percentage (0-100)
 */
export function calculatePortfolioOccupancy(properties: Property[]): number {
  if (!properties || properties.length === 0) return 0;

  const totalUnits = properties.reduce((sum, p) => sum + (p.units || 0), 0);

  if (totalUnits === 0) return 0;

  const occupiedUnits = properties.reduce((sum, p) => {
    const units = p.units || 0;
    const occupancyRate = p.occupancyRate || 0;
    return sum + (units * (occupancyRate / 100));
  }, 0);

  return Math.round((occupiedUnits / totalUnits) * 100);
}

/**
 * Calculate portfolio-level occupancy rate using direct unit data
 *
 * This calculates occupancy dynamically from the source of truth (Units)
 * rather than relying on potentially stale Property.occupancyRate fields.
 *
 * @param units - Array of units to calculate occupancy for
 * @returns Portfolio occupancy rate as a percentage (0-100)
 */
export function calculateOccupancyFromUnits(units: Unit[]): number {
  if (!units || units.length === 0) return 0;

  const occupiedUnits = units.filter(u => u.status === 'Occupied').length;
  const totalUnits = units.length;

  return totalUnits > 0 ? Math.round((occupiedUnits / totalUnits) * 100) : 0;
}

/**
 * Determine if a maintenance ticket/work order is completed
 *
 * Standardized definition: A job is considered completed if it has reached
 * one of the final successful states (Completed, Verified, or Approved).
 *
 * Note: 'Rejected' status is NOT considered completed - it indicates rework needed.
 *
 * @param ticket - Maintenance ticket to check
 * @returns True if the ticket is in a completed state
 */
export function isJobCompleted(ticket: MaintenanceTicket): boolean {
  if (!ticket?.status) return false;

  return ticket.status === 'Completed' ||
    ticket.status === 'Verified' ||
    ticket.status === 'Approved';
}

/**
 * Determine if a maintenance ticket/work order is active (in progress)
 *
 * A job is active if it's not completed and not rejected.
 *
 * @param ticket - Maintenance ticket to check
 * @returns True if the ticket is in an active state
 */
export function isJobActive(ticket: MaintenanceTicket): boolean {
  if (!ticket?.status) return false;
  return isWorkOrderActive(ticket.status);
}

/**
 * Calculate revenue from receivables with explicit filtering
 *
 * Provides two calculation modes:
 * - 'all': Total revenue (all receivables regardless of status)
 * - 'collected': Collected revenue (only paid receivables)
 *
 * This explicit distinction prevents confusion about which metric to use:
 * - Use 'all' for total revenue in dashboards and portfolio views
 * - Use 'collected' for cash-based statements and distribution calculations
 *
 * @param receivables - Array of receivables to calculate from
 * @param filter - Calculation mode ('all' or 'collected')
 * @returns Total revenue amount
 */
export function calculateRevenue(
  receivables: Receivable[],
  filter: 'all' | 'collected'
): number {
  if (!receivables || receivables.length === 0) return 0;

  if (filter === 'all') {
    return receivables.reduce((sum, r) => sum + (r.amountCents ?? 0) / 100, 0);
  }

  // 'collected' = Paid receivables only
  return receivables
    .filter(r => r.status === 'Paid')
    .reduce((sum, r) => sum + (r.amountCents ?? 0) / 100, 0);
}

/**
 * Calculate expenses from bills with explicit filtering
 *
 * Provides two calculation modes:
 * - 'all': Total expenses (all bills regardless of status)
 * - 'paid': Paid expenses (only paid bills)
 *
 * This explicit distinction prevents confusion about which metric to use:
 * - Use 'all' for total expenses in dashboards and portfolio views
 * - Use 'paid' for cash-based statements and distribution calculations
 *
 * @param bills - Array of bills to calculate from
 * @param filter - Calculation mode ('all' or 'paid')
 * @returns Total expense amount
 */
export function calculateExpenses(
  bills: Bill[],
  filter: 'all' | 'paid'
): number {
  if (!bills || bills.length === 0) return 0;

  if (filter === 'all') {
    return bills.reduce((sum, b) => sum + (b.amountCents ?? 0) / 100, 0);
  }

  // 'paid' = Paid bills only
  return bills
    .filter(b => b.status === 'Paid')
    .reduce((sum, b) => sum + (b.amountCents ?? 0) / 100, 0);
}

/**
 * Calculate Net Operating Income (NOI)
 *
 * NOI = Revenue - Expenses
 *
 * Can calculate both accrual-based (all) and cash-based (collected/paid) NOI.
 *
 * @param receivables - Array of receivables
 * @param bills - Array of bills
 * @param filter - Calculation mode ('all' or 'collected')
 * @returns Net operating income
 */
export function calculateNOI(
  receivables: Receivable[],
  bills: Bill[],
  filter: 'all' | 'collected'
): number {
  const revenue = calculateRevenue(receivables, filter);
  const expenses = calculateExpenses(bills, filter === 'all' ? 'all' : 'paid');

  return revenue - expenses;
}

/**
 * Calculate Return on Investment (ROI) percentage
 *
 * ROI = (Annual NOI / Total Investment) * 100
 *
 * @param annualNOI - Annual net operating income
 * @param totalInvestment - Total capital invested
 * @returns ROI as a percentage
 */
export function calculateROI(annualNOI: number, totalInvestment: number): number {
  if (!totalInvestment || totalInvestment === 0) return 0;

  return (annualNOI / totalInvestment) * 100;
}

/**
 * Calculate pending/outstanding receivables amount
 *
 * @param receivables - Array of receivables
 * @returns Total amount of unpaid receivables
 */
export function calculatePendingReceivables(receivables: Receivable[]): number {
  if (!receivables || receivables.length === 0) return 0;

  return receivables
    .filter(r => r.status !== 'Paid')
    .reduce((sum, r) => sum + (r.amountCents ?? 0) / 100, 0);
}

/**
 * Calculate overdue receivables amount
 *
 * @param receivables - Array of receivables
 * @returns Total amount of late/overdue receivables
 */
export function calculateOverdueReceivables(receivables: Receivable[]): number {
  if (!receivables || receivables.length === 0) return 0;

  return receivables
    .filter(r => r.status === 'Late')
    .reduce((sum, r) => sum + (r.amountCents ?? 0) / 100, 0);
}

/**
 * Count of late/overdue receivable rows (for nav badges — not dollar totals).
 */
export function calculateOverdueReceivableCount(receivables: Receivable[]): number {
  if (!receivables || receivables.length === 0) return 0;
  return receivables.filter((r) => r.status === 'Late').length;
}

/**
 * Calculate pending/unpaid bills amount
 *
 * @param bills - Array of bills
 * @returns Total amount of unpaid bills
 */
export function calculatePendingBills(bills: Bill[]): number {
  if (!bills || bills.length === 0) return 0;

  return bills
    .filter(b => b.status !== 'Paid')
    .reduce((sum, b) => sum + (b.amountCents ?? 0) / 100, 0);
}
